import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isTestModeActive } from "@/lib/testMode";

import SignupSuccess from "@/components/signup/SignupSuccess";
import SignupStep1Tier from "@/components/signup/SignupStep1Tier";
import SignupStep2Contact from "@/components/signup/SignupStep2Contact";
import SignupStep3Sign from "@/components/signup/SignupStep3Sign";
import { ADMIN_EMAIL, ADMIN_WAIT_TTL_MS } from "@/components/signup/constants";

const EMPTY_FORM = { company_name: "", owner_name: "", owner_title: "", email: "", phone: "", street_address: "", notes: "" };

export default function SignupModal({ open, onOpenChange }) {
  const [step, setStep] = useState(1);
  const [pricing, setPricing] = useState({ tiers: [], terms: "" });
  const [availability, setAvailability] = useState({});
  const [tier, setTier] = useState("2_meetings");
  const [tcAccepted, setTcAccepted] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [waitingList, setWaitingList] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [signature, setSignature] = useState("");
  const [selfQuals, setSelfQuals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [existingSignupId, setExistingSignupId] = useState(null);

  // Reset all state + load pricing/availability whenever the modal opens
  useEffect(() => {
    if (!open) return;
    setStep(1); setTcAccepted(false); setSelectedSlots([]); setWaitingList(false);
    setForm(EMPTY_FORM); setSignature(""); setSuccess(false); setSelfQuals([]);
    Promise.all([api.get(`/pricing${isTestModeActive() ? "?test_mode=1" : ""}`), api.get("/availability")])
      .then(([p, a]) => { setPricing(p.data); setAvailability(a.data); })
      .catch(() => toast.error("Could not load signup details."));
  }, [open]);

  // Detect prior signup via localStorage cookie so we can PUT (update) instead of POST (create).
  // Admin-side TTL prevents the WAIT card from sticking around between test submissions.
  useEffect(() => {
    if (!open) return;
    try {
      const id = localStorage.getItem("cvc_signup_id");
      const email = (localStorage.getItem("cvc_signup_email") || "").toLowerCase();
      const tsRaw = localStorage.getItem("cvc_signup_at");
      const ts = tsRaw ? parseInt(tsRaw, 10) : 0;
      const isAdmin = email === ADMIN_EMAIL.toLowerCase();
      const expired = isAdmin && ts && Date.now() - ts > ADMIN_WAIT_TTL_MS;
      if (expired) {
        localStorage.removeItem("cvc_signup_id");
        localStorage.removeItem("cvc_signup_email");
        localStorage.removeItem("cvc_signup_at");
        setExistingSignupId(null);
      } else {
        setExistingSignupId(id || null);
      }
    } catch { setExistingSignupId(null); }
  }, [open]);

  const openSlots = useMemo(
    () => Object.entries(availability).filter(([, v]) => v).map(([k]) => k),
    [availability]
  );
  const anyOpen = openSlots.length > 0;

  // When the calendar's totally closed, default to waiting list
  useEffect(() => { if (!anyOpen) setWaitingList(true); }, [anyOpen]);

  const tierInfo = pricing.tiers.find((t) => t.id === tier) || pricing.tiers[0];
  const isUpdating = !!existingSignupId;

  const toggleSlot = (key) => {
    if (waitingList) setWaitingList(false);
    setSelectedSlots((s) => s.includes(key) ? s.filter((x) => x !== key) : [...s, key]);
  };
  const onToggleWaitingList = (val) => {
    setWaitingList(!!val);
    if (val) setSelectedSlots([]);
  };
  const toggleQual = (id) => setSelfQuals((q) => q.includes(id) ? q.filter((x) => x !== id) : [...q, id]);

  const validate = () => {
    if (!tcAccepted) return "Please accept the Terms & Conditions to continue.";
    if (!form.company_name || !form.owner_name || !form.email || !form.phone) return "Please fill in all required contact fields.";
    if (!form.street_address) return "Please enter your street address.";
    if (!waitingList && selectedSlots.length === 0) return "Please pick at least one preferred meeting time, or join the waiting list.";
    if (!signature.trim()) return "Please type your name as your digital signature.";
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tier,
        monthly_budget: tierInfo?.monthly_total || 0,
        first_last_retainer: tierInfo?.first_last_retainer || 0,
        signature_typed: signature.trim(),
        preferred_slots: waitingList ? [] : selectedSlots,
        waiting_list: waitingList,
        tc_accepted: tcAccepted,
        self_quals: selfQuals,
        is_test_mode: isTestModeActive(),
      };
      let result;
      if (existingSignupId) {
        try {
          ({ data: result } = await api.put(`/signups/${existingSignupId}`, payload));
        } catch (e) {
          // Fall back to a fresh create if the prior signup no longer exists or is locked
          if (e.response?.status === 404) {
            ({ data: result } = await api.post("/signups", payload));
          } else { throw e; }
        }
      } else {
        ({ data: result } = await api.post("/signups", payload));
      }
      try {
        if (result?.id) {
          localStorage.setItem("cvc_signup_id", result.id);
          localStorage.setItem("cvc_signup_email", (form.email || "").toLowerCase());
          localStorage.setItem("cvc_signup_at", String(Date.now()));
        }
      } catch { /* ignore: private mode / storage disabled */ }
      setSuccess(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="signup-modal"
        className="max-w-3xl max-h-[92vh] overflow-y-auto bg-cvc-cream border-cvc-border p-0"
      >
        {success ? (
          <SignupSuccess isUpdating={isUpdating} onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-4">
              {pricing.test_mode?.enabled && (
                <div data-testid="signup-modal-test-banner" className="mb-3 rounded-md bg-amber-100 border border-amber-300 px-3 py-2 text-xs font-body text-amber-900">
                  <strong>🧪 TEST MODE</strong> — prices shown are reduced to $2 / $3 for testing. Real customers will see normal pricing.
                  {step === 1 && (
                    <span className="block mt-1 italic">
                      Are you a real customer who hit this page by chance? Hang tight a few moments, then refresh your page — it'll be worth the wait.
                    </span>
                  )}
                </div>
              )}
              <p className="text-xs tracking-[0.25em] uppercase font-body font-semibold text-cvc-red">Step {step} of 3 · Get Scaled Up</p>
              <DialogTitle className="font-heading text-4xl text-cvc-navy mt-2">
                {step === 1 && "When should Jonathan come by?"}
                {step === 2 && "Choose your engagement"}
                {step === 3 && "Confirm and sign"}
              </DialogTitle>
              <DialogDescription className="font-body text-cvc-ink-soft">
                {step === 1 && "Tell us about your company and pick your preferred on-site meeting windows."}
                {step === 2 && "Pick a meeting cadence and accept the engagement terms."}
                {step === 3 && "A few last details, then your digital signature."}
              </DialogDescription>
              {step === 1 && isUpdating && (
                <div data-testid="update-wait-card" className="mt-4 rounded-lg border-2 border-cvc-red bg-cvc-red/5 px-4 py-3">
                  <p className="text-[11px] tracking-[0.25em] uppercase font-body font-bold text-cvc-red">⚠ Wait.</p>
                  <p className="font-body text-sm text-cvc-ink mt-1.5 leading-relaxed">
                    You're already signed up and awaiting approval. Fill out again only if you need to make changes. Your original application will be replaced by this new one.
                  </p>
                </div>
              )}
            </DialogHeader>
            <Separator />

            <div className="px-8 pt-3 pb-6 space-y-6">
              {/* Step ordering: (1) Contact + Slots  →  (2) Tier + Terms  →  (3) Review + Sign.
                  The component names still say Step1Tier / Step2Contact for historical reasons —
                  only the rendered STEP NUMBER swapped. */}
              {step === 1 && (
                <SignupStep2Contact
                  form={form} setForm={setForm}
                  availability={availability}
                  selectedSlots={selectedSlots} toggleSlot={toggleSlot}
                  waitingList={waitingList} onToggleWaitingList={onToggleWaitingList}
                  anyOpen={anyOpen}
                  selfQuals={selfQuals} toggleQual={toggleQual}
                  onBack={null}
                  onContinue={() => setStep(2)}
                />
              )}
              {step === 2 && (
                <SignupStep1Tier
                  pricing={pricing}
                  tier={tier} setTier={setTier}
                  tcAccepted={tcAccepted} setTcAccepted={setTcAccepted}
                  onBack={() => setStep(1)}
                  onContinue={() => setStep(3)}
                />
              )}
              {step === 3 && (
                <SignupStep3Sign
                  tierInfo={tierInfo}
                  waitingList={waitingList} selectedSlots={selectedSlots}
                  form={form} setForm={setForm}
                  signature={signature} setSignature={setSignature}
                  submitting={submitting} isUpdating={isUpdating}
                  onBack={() => setStep(2)}
                  onSubmit={submit}
                />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
